<template>
  <SelectableGridInput v-model="selectedFontId" :items="fonts">
    <template>
      <h2 class="text-xl font-semibold">Choose a font style</h2>
    </template>

    <template #item="{ item }: { item: FontOption }">
      <div class="w-full">
        <p class="text-sm text-base-content/70 mb-1">{{ item.label }}</p>
        <p :style="{ fontFamily: String(item.family || 'sans-serif') }" class="text-lg text-base-content">
          {{ t(`ui.inputs.fontexample`) }}
        </p>
      </div>
    </template>
  </SelectableGridInput>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import SelectableGridInput from "./SelectableGridInput.vue";
import { SelectableItemDto } from "@/features/blueprint/dtos/SelectableItemDto";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

interface FontOption extends SelectableItemDto {
  label: string;
  family: string;
  importUrl?: string;
}

const props = defineProps<{
  modelValue: string | null;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const fonts: FontOption[] = [
  {
    id: "inter",
    label: "Inter",
    family: "'Inter', sans-serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Inter&display=swap",
  },
  {
    id: "playfair",
    label: "Playfair Display",
    family: "'Playfair Display', serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Playfair+Display&display=swap",
  },
  {
    id: "poppins",
    label: "Poppins",
    family: "'Poppins', sans-serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Poppins&display=swap",
  },
  {
    id: "raleway",
    label: "Raleway",
    family: "'Raleway', sans-serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Raleway&display=swap",
  },
  {
    id: "lora",
    label: "Lora",
    family: "'Lora', serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Lora&display=swap",
  },
  {
    id: "montserrat",
    label: "Montserrat",
    family: "'Montserrat', sans-serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Montserrat&display=swap",
  },
  {
    id: "source-serif",
    label: "Source Serif Pro",
    family: "'Source Serif Pro', serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Source+Serif+Pro&display=swap",
  },
  {
    id: "roboto",
    label: "Roboto",
    family: "'Roboto', sans-serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Roboto&display=swap",
  },
  {
    id: "open-sans",
    label: "Open Sans",
    family: "'Open Sans', sans-serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Open+Sans&display=swap",
  },
  {
    id: "merriweather",
    label: "Merriweather",
    family: "'Merriweather', serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Merriweather&display=swap",
  },
  {
    id: "nunito",
    label: "Nunito",
    family: "'Nunito', sans-serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Nunito&display=swap",
  },
  {
    id: "work-sans",
    label: "Work Sans",
    family: "'Work Sans', sans-serif",
    importUrl: "https://fonts.googleapis.com/css2?family=Work+Sans&display=swap",
  },
];

const selectedFontId = ref<string | null>(props.modelValue ?? fonts[0].id);

watch(
  () => props.modelValue,
  val => {
    selectedFontId.value = val ?? fonts[0].id;
  }
);

watch(selectedFontId, id => {
  emit("update:modelValue", id!);
});

function loadFont(font: FontOption) {
  if (!font.importUrl) return;
  const exists = document.querySelector(`link[href="${font.importUrl}"]`);
  if (!exists) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = font.importUrl;
    document.head.appendChild(link);
  }
}

onMounted(() => {
  fonts.forEach(loadFont);
});
</script>
